/**
 * `demo/guide/chapters.ts` — the demo guide's chapters (#2824).
 *
 * The introduction runs first and names each page from the nav. Then the
 * visitor explores: each page has its own short tour, which opens the first
 * time the visitor opens that page. A tour never moves the visitor to another
 * page. The Setup tours are built from the picked scenario's `considerations`
 * (`GET /api/demo/scenarios`): one tour per Setup tab, one step per
 * consideration on that tab. The words are drafts for the founder to review.
 */
import type { DemoSessionChange } from "../../../api/useDemoSessionState";
import { DEMO_CHANGE_CONTROL_ANCHOR, PEOPLE_ADD_MEMBER_ANCHOR } from "../DemoChangedTag";
import type { GuideChapter, GuideContext, GuideStep } from "./types";

export interface GuideConsideration {
  text: string;
  /** Setup tab path, such as `stations` or `rules`. */
  setup_tab: string;
  /** The Setup element the step points at. */
  anchor: string;
}

/** One stored change of the visitor's scenario (`GET /api/demo-sessions/current`). */
export type GuideChange = DemoSessionChange;

export type ChapterMap = Record<string, GuideChapter>;

const onTeams = (p: string) => p === "/" || p === "";
const inTeam = (p: string) => /^\/teams\/[^/]+/.test(p);
const anywhere = () => true;

const teamHome = ({ teamId }: { teamId: string }) => `/teams/${teamId}`;

export const INTRO_ID = "intro";
export const ROSTER_ID = "roster";
export const TEAM_ID = "team";

/** The id of the tour of one picked scenario, such as `scenario:sick_day`. */
export function scenarioTourId(variant: string): string {
  return `scenario:${variant}`;
}

/** The id of the tour on one Setup tab, such as `setup:stations`. */
export function setupTourId(tab: string): string {
  return `setup:${tab}`;
}

const INTRO_STEPS: GuideStep[] = [
  {
    id: "teams",
    route: onTeams,
    openPath: () => "/",
    openLabel: "Open the teams overview",
    anchors: ["team-tile"],
    title: "Your teams",
    body: "View the teams you manage here, on the teams overview.",
    done: { type: "next" },
  },
  {
    id: "logo",
    route: anywhere,
    anchors: ["logo"],
    title: "The Tarii logo",
    body: "Press the **Tarii logo** to come back here from any page.",
    done: { type: "next" },
  },
  {
    id: "switcher",
    route: onTeams,
    openPath: () => "/",
    openLabel: "Open the teams overview",
    anchors: ["team-switcher"],
    title: "Switch teams",
    body: "Or switch teams here.",
    done: { type: "next" },
  },
  {
    id: "open-team",
    route: onTeams,
    openPath: () => "/",
    openLabel: "Open the teams overview",
    anchors: ["team-tile"],
    title: "Open your team",
    body: "Press **your team** to see its pages.",
    done: { type: "route", to: inTeam, go: teamHome, goLabel: "Open your team" },
  },
  {
    id: "nav-home",
    route: inTeam,
    openPath: teamHome,
    openLabel: "Open your team",
    anchors: ["nav-home"],
    title: "Home",
    body: "Open **Home** to see an overview of your duties. Outstanding tasks are flagged out.",
    done: { type: "next" },
  },
  {
    id: "nav-roster",
    route: inTeam,
    openPath: teamHome,
    openLabel: "Open your team",
    anchors: ["nav-roster"],
    title: "Roster",
    body: "Open **Roster**. The week is solved and published from there.",
    done: { type: "next" },
  },
  {
    id: "nav-people",
    route: inTeam,
    openPath: teamHome,
    openLabel: "Open your team",
    anchors: ["nav-people"],
    title: "People",
    body: "Open **People** to see the people on the team.",
    done: { type: "next" },
  },
  {
    id: "nav-team",
    route: inTeam,
    openPath: teamHome,
    openLabel: "Open your team",
    anchors: ["nav-team"],
    title: "Team",
    body: "Open **Team** to see who is on which station, who can sign in, and every change to the team.",
    done: { type: "next" },
  },
  {
    id: "nav-setup",
    route: inTeam,
    openPath: teamHome,
    openLabel: "Open your team",
    anchors: ["nav-setup"],
    title: "Setup",
    body: "Open **Setup** for stations, rules and imports.",
    done: { type: "next" },
  },
  {
    id: "intro-end",
    route: inTeam,
    openPath: teamHome,
    openLabel: "Open your team",
    title: "That is the introduction",
    body: "That is the end of the introduction. Now press any page in the menu: each page shows its own short tour the first time you open it.",
    done: { type: "next" },
    nextLabel: "Explore on my own",
  },
];

/** The short name of each Setup element the scenarios point at. */
const SETTING_TITLES: Record<string, string> = {
  "control-room": "Control room stations",
  "post-call-rest": "Post-call rest day",
  "call-points": "Call points",
  standby: "Standby stations",
  "leave-quota": "Daily leave quota",
  stations: "Stations",
  "level-floors": "Staff floor per station",
  "fte-fairness": "FTE-weighted load",
  "call-balance": "On-call frequency",
  swaps: "Swap requests",
};

const TAB_TITLES: Record<string, string> = {
  stations: "Stations",
  rules: "Rules & fairness",
  components: "Components",
};

/** Other elements that show the same setting when the first one is not on the page. */
const ANCHOR_ALIASES: Record<string, string[]> = {
  "fte-fairness": ["fte-fairness", "fairness-summary"],
  "call-balance": ["call-balance", "fairness-summary"],
};

/** The nav link of a Setup tab: where a step lands when its element is missing. */
export function setupTabAnchor(tab: string): string {
  return `setup-tab-${tab}`;
}

const onSetupTab = (tab: string) => (p: string) =>
  new RegExp(`^/teams/[^/]+/setup/${tab}(/|$)`).test(p);

function setupStep(c: GuideConsideration, index: number): GuideStep {
  const tab = c.setup_tab;
  return {
    id: `${index}-${c.anchor}`,
    route: onSetupTab(tab),
    openPath: ({ teamId }) => `/teams/${teamId}/setup/${tab}`,
    openLabel: "Open Setup",
    anchors: ANCHOR_ALIASES[c.anchor] ?? [c.anchor],
    fallbackAnchors: [setupTabAnchor(tab)],
    title: SETTING_TITLES[c.anchor] ?? TAB_TITLES[tab] ?? "Setup",
    body: c.text,
    done: { type: "next" },
  };
}

function pageStep(
  tab: string,
  id: string,
  title: string,
  body: string,
  anchors: string[],
): GuideStep {
  return {
    id: `${tab}-${id}`,
    route: onSetupTab(tab),
    openPath: ({ teamId }) => `/teams/${teamId}/setup/${tab}`,
    openLabel: "Open Setup",
    anchors,
    fallbackAnchors: [setupTabAnchor(tab)],
    title,
    body,
    done: { type: "next" },
  };
}

/** The fixed steps of the Stations, Attributes and Rules & fairness tours: a short
 * explanation of the page first, then the list it shows. Setup is view-only in the
 * demo, so no step asks the visitor to change anything. */
const SETUP_PAGE_STEPS: Record<string, GuideStep[]> = {
  stations: [
    pageStep(
      "stations",
      "overview",
      "Stations",
      "Stations are the places people work. A lead sets how many people each shift needs at each one. The demo only shows this.",
      [setupTabAnchor("stations")],
    ),
  ],
  attributes: [
    pageStep(
      "attributes",
      "overview",
      "Attributes",
      "Attributes are the skills and traits a station can ask for, such as a licence or a level. A lead sets them once, and the roster keeps to them. The demo only shows this.",
      [setupTabAnchor("attributes")],
    ),
    pageStep(
      "attributes",
      "list",
      "Attribute list",
      "Each row is one attribute. A station can ask for it on every shift.",
      ["attributes-list"],
    ),
  ],
  rules: [
    pageStep(
      "rules",
      "overview",
      "Rules & fairness",
      "Rules are what the roster must always keep. Fairness is how the work gets shared out. A lead sets both here. The demo only shows this.",
      [setupTabAnchor("rules")],
    ),
    pageStep(
      "rules",
      "list",
      "Rules list",
      "Each row is one rule, such as a rest day after a night on call.",
      ["rules-list"],
    ),
  ],
};

/** Shown after the overview when the scenario adds no station step of its own. */
const STATIONS_LIST_STEP = pageStep(
  "stations",
  "list",
  "Station list",
  "Each row is one station, with the fewest people each shift needs there.",
  ["stations"],
);

const onRoster = (p: string) => /^\/teams\/[^/]+\/roster(\/|$)/.test(p);
const rosterPath = ({ teamId }: { teamId: string }) => `/teams/${teamId}/roster`;

/** The roster page, in the order a lead works it. The demo takes no hand edits,
 * so the tour has no Edit step (#2824). */
const ROSTER_STEPS: GuideStep[] = [
  {
    id: "roster-solve",
    route: onRoster,
    openPath: rosterPath,
    openLabel: "Open the roster",
    anchors: ["roster-solve"],
    title: "Solve the roster",
    body: "Press **Solve**. The tour goes on when the roster is in.",
    done: { type: "action", action: "solved" },
  },
  {
    id: "roster-publish",
    route: onRoster,
    openPath: rosterPath,
    openLabel: "Open the roster",
    // The Publish button, else the More menu that holds Publish (#2824).
    anchors: ["roster-publish", "roster-publish-more"],
    preferFirstAnchor: true,
    title: "Publish roster",
    body: "Press **Publish roster** and confirm. The roster goes to your team.",
    done: { type: "action", action: "published" },
  },
  {
    id: "roster-phone",
    route: onRoster,
    openPath: rosterPath,
    openLabel: "Open the roster",
    anchors: ["demo-phone-toggle", "demo-phone"],
    title: "See it on a phone",
    body: "Press **Phone view** to see the roster on a staff member's phone. Pick a person at the top of the phone.",
    done: { type: "action", action: "phone-opened", stepAside: true, nextWhenOnly: "demo-phone" },
  },
];

const onTeamTab = (tab: string) => (p: string) =>
  new RegExp(`^/teams/[^/]+/team/${tab}(/|$)`).test(p);
const onTeamPage = (p: string) => /^\/teams\/[^/]+\/team(\/|$)/.test(p);
const teamTabPath =
  (tab: string) =>
  ({ teamId }: { teamId: string }) =>
    `/teams/${teamId}/team/${tab}`;

/** The Team page: Stations, Access and Activity, as a lead sees them. */
const TEAM_STEPS: GuideStep[] = [
  {
    id: "team-stations",
    route: onTeamTab("stations"),
    openPath: teamTabPath("stations"),
    openLabel: "Open Stations",
    anchors: ["team-stations", "team-tab-stations"],
    title: "Stations",
    body: "See who is on which station for a shift, taken from the roster. Use the **arrows** to look at another day.",
    done: { type: "next" },
  },
  {
    id: "team-access",
    route: onTeamTab("access"),
    openPath: teamTabPath("access"),
    openLabel: "Open Access",
    anchors: ["team-access", "team-tab-access"],
    title: "Who can sign in",
    body: "Everyone on the team, who has joined and who added them. A lead gives, changes and removes access here. In the demo you can look but not change.",
    done: { type: "next" },
  },
  {
    id: "team-activity",
    route: onTeamTab("activity"),
    openPath: teamTabPath("activity"),
    openLabel: "Open Activity",
    anchors: ["team-activity", "team-tab-activity"],
    title: "What changed",
    body: "Every change to the team in plain words, newest first: who was added, what was published, who was called in. Use the filter at the top to pick a kind.",
    done: { type: "next" },
    nextLabel: "Explore on my own",
  },
];

/**
 * After Publish roster the visitor can pick a scenario from the top bar. The
 * step opens the picker (#2962): Autoplay presses Change the scenario as the
 * bar ends, and the card's button does the same. The tour ends there.
 */
const SCENARIO_STEP: GuideStep = {
  id: "roster-scenario",
  route: () => true,
  anchors: ["nav-change-scenario"],
  title: "Change the scenario",
  body: "Press **Change the scenario** to pick one and see how Tarii handles a sick day, leave or a swap.",
  done: { type: "action", action: "scenario-opened" },
  pressLabel: "Open the scenarios",
  lockedBody:
    "**Change the scenario** opens once the roster is solved. Solve it first, then pick one to see how Tarii handles a sick day, leave or a swap.",
  nextLabel: "Explore on my own",
};

const MONTHS = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

function parts(iso: string): { d: number; m: number } | null {
  const hit = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
  return hit ? { d: Number(hit[3]), m: Number(hit[2]) - 1 } : null;
}

/** "29 October", "29 and 30 October", "29 to 31 October" or "31 October to 1 November". */
export function formatDates(start: string | null, end: string | null): string | null {
  const a = start ? parts(start) : null;
  if (!start || !a) return null;
  const b = end ? parts(end) : null;
  if (!end || !b || end <= start) return `${a.d} ${MONTHS[a.m]}`;
  if (a.m !== b.m) return `${a.d} ${MONTHS[a.m]} to ${b.d} ${MONTHS[b.m]}`;
  const days = Math.round((Date.parse(end) - Date.parse(start)) / 86_400_000);
  return `${a.d}${days === 1 ? " and " : " to "}${b.d} ${MONTHS[b.m]}`;
}

const teamHomeRoute = (p: string) => /^\/teams\/[^/]+\/?$/.test(p);
const onPeople = (p: string) => /^\/teams\/[^/]+\/members(\/|$)/.test(p);
const onStations = onSetupTab("stations");

type StepSeed = Pick<GuideStep, "id" | "title" | "body" | "done"> & Partial<GuideStep>;

const rosterStep = (s: StepSeed): GuideStep => ({
  route: onRoster,
  openPath: rosterPath,
  openLabel: "Open the roster",
  ...s,
});

const homeStep = (id: string, title: string, body: string): GuideStep => ({
  id,
  route: teamHomeRoute,
  openPath: teamHome,
  openLabel: "Open Home",
  anchors: ["needs-you", "leave-requests"],
  title,
  body,
  done: { type: "next" },
});

const phoneSendStep = (id: string, title: string, body: string): GuideStep =>
  rosterStep({
    id,
    anchors: ["demo-phone"],
    title,
    body,
    done: { type: "action", action: "request-sent" },
  });

const solveStep = rosterStep({
  id: "solve",
  anchors: ["roster-solve"],
  title: "Solve",
  body: "Press **Solve**. Tarii finds cover and keeps the published roster until you publish.",
  done: { type: "action", action: "solved" },
});

/** After the Solve: the words say what moved, the ringed cells on the roster show where. */
const solveChangesStep = rosterStep({
  id: "solve-changes",
  anchors: ["demo-solve-first-change"],
  fallbackAnchors: ["roster-grid", "roster-solve"],
  title: "What the solve changed",
  body: "Working out what moved…",
  liveBody: "solve-changes",
  done: { type: "next" },
});

const publishStep = rosterStep({
  id: "publish",
  // The Publish button, else the More menu that holds Publish (#2824).
  anchors: ["roster-publish", "roster-publish-more"],
  preferFirstAnchor: true,
  title: "Publish roster",
  body: "Press **Publish roster** and confirm. The roster goes to your team.",
  done: { type: "action", action: "published" },
});

/** After Publish: every tour sends the visitor to the phone's Inbox for the update. */
const PHONE_INBOX = "Press **Phone view**, then tap the **Inbox** tab: the update is there.";

const phoneStep = (body: string): GuideStep =>
  rosterStep({
    id: "phone",
    anchors: ["demo-phone-toggle", "demo-phone"],
    title: "On the phone",
    body: `${PHONE_INBOX} ${body}`,
    done: { type: "next" },
    nextLabel: "Explore on my own",
  });

const acceptStep = (id: string, title: string, body: string): GuideStep => ({
  id,
  route: teamHomeRoute,
  openPath: teamHome,
  openLabel: "Open Home",
  anchors: ["leave-review-accept"],
  fallbackAnchors: ["needs-you", "leave-requests"],
  title,
  body,
  done: { type: "action", action: "request-accepted" },
});

/** The steps of one stored change, from the template of its kind. */
export function scenarioSteps(change: GuideChange): GuideStep[] {
  const who = change.member ?? "A team member";
  const dates = formatDates(change.start ?? null, change.end ?? null);
  const on = dates ? ` on ${dates}` : "";
  if (change.id === "sick_day") {
    return [
      phoneSendStep(
        "sick",
        "A sick day",
        `${who} wakes up unwell. On the phone, tap **Tasks**, then **+ Report off sick**, then **Report off sick**.`,
      ),
      homeStep("home", "Home", `${who}'s sick day shows under Needs you once sent.`),
      rosterStep({
        id: "roster",
        title: "The roster",
        anchors: ["demo-subject"],
        fallbackAnchors: ["review-cover"],
        body: `${who}'s row on the roster. Their shifts${on} now read off sick.`,
        done: { type: "next" },
      }),
      // A sick day binds when the phone sends it: Home has nothing to accept.
      homeStep(
        "record",
        "Recorded at once",
        `${who}'s sick day took effect when the phone sent it. Sick days are recorded, never declined, so it needs no approval. The next solve finds cover.`,
      ),
      solveStep,
      solveChangesStep,
      publishStep,
      phoneStep(`${who}'s phone shows the days off. The person who covers sees the new shift.`),
    ];
  }
  if (change.id === "leave") {
    return [
      phoneSendStep(
        "leave",
        "A leave request",
        `${who} asks for leave${on}. On the phone, tap **Tasks**, then **+ Request leave**, then **Submit leave**.`,
      ),
      homeStep("home", "Home", `${who}'s leave shows under Needs you once sent.`),
      acceptStep(
        "requests",
        "Leave and requests",
        `On Home, under Leave and requests, press **Accept into plan** on ${who}'s leave. Accepted leave goes into the next solve.`,
      ),
      solveStep,
      solveChangesStep,
      publishStep,
      phoneStep(`${who}'s phone shows the leave. The person who covers sees the new shift.`),
    ];
  }
  if (change.id === "swap") {
    return [
      phoneSendStep(
        "swap",
        "A swap",
        `${who} gives a shift away${on}. On the phone, tap that shift on the **Calendar**, then **Request swap**, then **Send request**.`,
      ),
      homeStep("home", "Home", "The swap shows under Needs you once sent."),
      {
        id: "confirm",
        // The swap list opens first, so the card lands once, on Confirm (#2824).
        route: (p, q = "") => onRoster(p) && /(^|[?&])surface=swaps(&|$)/.test(q),
        openPath: ({ teamId }) => `/teams/${teamId}/roster?surface=swaps`,
        openLabel: "Open the swaps",
        anchors: ["swap-confirm"],
        title: "Confirm it",
        body: "Press **Confirm** on the swap. The shift moves to the new person on the roster at once.",
        done: { type: "action", action: "swap-confirmed" },
      },
      phoneStep(`${who}'s phone shows the new shift.`),
    ];
  }
  const target = change.target ?? null;
  const people = target ? target.kind !== "station" : change.id !== "station_condition";
  // A card is open only with a person in the address: a new member has no id
  // until it is added, so the step opens the card again once the id is known.
  const onPersonCard = (p: string, q = "") => onPeople(p) && /(^|[?&])person=[^&]/.test(q);
  const onStationDrawer = (p: string, q = "") => onStations(p) && /(^|[?&])station=/.test(q);
  const personPath = ({ teamId }: GuideContext) =>
    `/teams/${teamId}/members?person=${encodeURIComponent(target?.member_id ?? "")}`;
  const stationPath = ({ teamId }: GuideContext) =>
    `/teams/${teamId}/setup/stations?station=${encodeURIComponent(target?.station ?? "")}`;
  const isNew = target?.kind === "new_member";
  const hasCard = people ? Boolean(target?.member_id) || isNew : Boolean(target?.station);
  const cardName = people ? (target?.member ?? who) : (target?.station ?? "the station");
  const openCardLabel = people ? `Open ${cardName}'s card` : `Open ${cardName} in Stations`;
  // #2824 - the visitor makes the change on the real controls; the ring moves
  // to the control to use next (the level, the chips, Review & apply, Apply).
  const makeBody = people
    ? isNew
      ? `On ${cardName}'s card, set **Level** under **Basics**, then press the chips under **Can work at** that are named below.`
      : `On ${cardName}'s card, under **Can work at**, press the chips until only those named below are ticked.`
    : `In ${cardName}, **Floor / session** holds the suggested number. Press **Review & apply**, then **Apply**.`;
  return [
    {
      id: "changed",
      route: people ? onPeople : onStations,
      openPath: ({ teamId }) =>
        people ? `/teams/${teamId}/members` : `/teams/${teamId}/setup/stations`,
      openLabel: people ? "Open People" : "Open Stations",
      anchors: ["demo-changed"],
      fallbackAnchors: people ? ["nav-people"] : [setupTabAnchor("stations"), "stations"],
      title: "What changed",
      body: change.target?.explain ?? change.label,
      done: { type: "next" },
      ...(hasCard && !isNew ? { nextLabel: openCardLabel } : {}),
    },
    ...(isNew
      ? [
          {
            id: "add-member",
            route: onPeople,
            openPath: ({ teamId }: GuideContext) => `/teams/${teamId}/members`,
            openLabel: "Open People",
            anchors: [PEOPLE_ADD_MEMBER_ANCHOR],
            fallbackAnchors: ["nav-people"],
            title: "Add the person",
            body: `Press **More**, then **Add manual member**, then **Add member**. The name ${cardName} is filled in.`,
            done: { type: "action" as const, action: "member-added" as const },
          },
        ]
      : []),
    ...(hasCard
      ? [
          {
            id: "make-change",
            route: people ? onPersonCard : onStationDrawer,
            openPath: people ? personPath : stationPath,
            openLabel: openCardLabel,
            anchors: [DEMO_CHANGE_CONTROL_ANCHOR],
            fallbackAnchors: people ? ["nav-people"] : [setupTabAnchor("stations"), "stations"],
            title: "Make the change",
            body: makeBody,
            done: { type: "action" as const, action: "change-made" as const, stepAside: false },
          },
        ]
      : []),
    solveStep,
    solveChangesStep,
    publishStep,
    phoneStep(
      change.member ? `${who}'s phone shows the new roster.` : "The phone shows the new roster.",
    ),
  ];
}

/** The introduction, the page tours and one tour per stored change. The Stations, Attributes and
 * Rules & fairness tours always exist and open with a short page explanation. Another Setup tab
 * with no considerations has no tour, and the roster tour points at the
 * scenario button only when the scenario has stored changes. */
export function buildChapters(
  considerations: GuideConsideration[] = [],
  changes: GuideChange[] = [],
): ChapterMap {
  const out: ChapterMap = { [INTRO_ID]: { id: INTRO_ID, kind: "intro", steps: INTRO_STEPS } };
  const tabs = [
    ...new Set([...Object.keys(SETUP_PAGE_STEPS), ...considerations.map((c) => c.setup_tab)]),
  ];
  for (const tab of tabs) {
    const id = setupTourId(tab);
    const own = considerations
      .map((c, index) => ({ c, index }))
      .filter(({ c }) => c.setup_tab === tab)
      .map(({ c, index }) => setupStep(c, index));
    const fixed = SETUP_PAGE_STEPS[tab] ?? [];
    const steps = [
      ...fixed,
      ...(tab === "stations" && own.length === 0 ? [STATIONS_LIST_STEP] : []),
      ...own,
    ];
    out[id] = { id, kind: "page", steps, page: onSetupTab(tab) };
  }
  out[ROSTER_ID] = {
    id: ROSTER_ID,
    kind: "page",
    steps:
      changes.length > 0
        ? [...ROSTER_STEPS.map(({ nextLabel: _drop, ...rest }) => rest), SCENARIO_STEP]
        : ROSTER_STEPS,
    page: onRoster,
  };
  out[TEAM_ID] = { id: TEAM_ID, kind: "page", steps: TEAM_STEPS, page: onTeamPage };
  for (const change of changes) {
    const id = scenarioTourId(change.id);
    out[id] = {
      id,
      kind: "scenario",
      steps: scenarioSteps(change),
      memberId: change.member_id ?? null,
      // The person whose phone the last step opens: for a joiner, the new
      // member once the session knows their id (after Add member, #2824).
      phoneMemberId: change.member_id ?? change.target?.member_id ?? null,
      label: change.label,
    };
  }
  return out;
}
